import React, {
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useColorScheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Apps as AppsIcon,
  BarChart as BarChartIcon,
  Bolt as BoltIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  EmojiEvents as EmojiEventsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FormatQuote as FormatQuoteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Link as LinkIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  MenuBook as MenuBookIcon,
  Merge as MergeIcon,
  Person as PersonIcon,
  SettingsOutlined as SettingsIcon,
  SignalCellularAlt as SignalCellularAltIcon,
  TrendingUp as TrendingUpIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { isPathEqualToRoute, pagePaths } from 'routes/paths';
import UIText from 'components/ui/UIText';
import UIFlex from 'components/ui/UIFlex';
import UIContainer from 'components/ui/UIContainer';
import UICard from 'components/ui/UICard';
import AppLanguageSelect from 'components/AppLanguageSelect';
import Logo from 'assets/logo.svg?react';
import DarkThemeIcon from 'assets/dark_theme.svg?react';
import LightThemeIcon from 'assets/light_theme.svg?react';
import { axiosInstance } from 'setup/axiosInstance';
import { UserProfile } from 'app/types';
import { AuthContext } from 'AuthProvider';

interface IAppHeaderProps {}

const toUrl = (path: string) => `/${path}`;

// ─────────────────────────────────────────────────────────────────────────────
// Menu data — icon-enriched, mirrors the explorations/header-redesign prototype.
// `wordSetMenuGroups` powers the 2-column desktop mega-menu (themed: "Словник"
// vs "Вирази"). The flat `wordSetMenuItems` is what the drawer accordion uses.
// ─────────────────────────────────────────────────────────────────────────────

type MenuIconComponent = typeof PersonIcon;

interface MenuItem {
  label: string;
  to: string;
  Icon: MenuIconComponent;
  desc?: string;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

const wordSetMenuGroups: MenuGroup[] = [
  {
    title: 'Слова',
    items: [
      {
        Icon: SignalCellularAltIcon,
        label: 'Англійська лексика за рівнями',
        desc: 'A1 – C2, від простого до складного',
        to: toUrl(pagePaths.levelBased),
      },
      {
        Icon: CategoryIcon,
        label: 'Тематична англійська лексика',
        desc: 'Слова за темами та ситуаціями',
        to: toUrl(pagePaths.topicRelated),
      },
      {
        Icon: TrendingUpIcon,
        label: 'Найбільш поширені слова',
        desc: 'Ядро мови — найвживаніша лексика',
        to: toUrl(pagePaths.mostCommon),
      },
    ],
  },
  {
    title: 'Вирази',
    items: [
      {
        Icon: MergeIcon,
        label: 'Фразові дієслова',
        desc: 'get up, look for, give in…',
        to: toUrl('wordset/phrasal-verbs'),
      },
      {
        Icon: LinkIcon,
        label: 'Словосполучення',
        desc: 'Природні поєднання слів',
        to: toUrl('wordset/collocations'),
      },
      {
        Icon: FormatQuoteIcon,
        label: 'Ідіоми та вирази',
        desc: 'Переносні значення й сталі звороти',
        to: toUrl('wordset/idioms'),
      },
    ],
  },
  {
    title: 'Неправильні дієслова',
    items: [
      {
        Icon: BoltIcon,
        label: 'Неправильні дієслова',
        desc: 'Три форми, що варто знати напам’ять',
        to: toUrl(pagePaths.irregularVerb),
      },
    ],
  },
];

const wordSetMenuItems: MenuItem[] = wordSetMenuGroups.flatMap((g) => g.items);

const vocabularyMenuItems: MenuItem[] = [
  {
    Icon: MenuBookIcon,
    label: 'Мій словник слів',
    to: toUrl(pagePaths.vocabularyWords),
  },
  {
    Icon: BoltIcon,
    label: 'Мій словник неправильних дієслів',
    to: toUrl(pagePaths.vocabularyVerbs),
  },
  {
    Icon: LinkIcon,
    label: 'Мій словник фраз і словосполучень',
    to: toUrl(pagePaths.vocabularyExpressions),
  },
];

const profileMenuItems: MenuItem[] = [
  {
    Icon: PersonIcon,
    label: 'Профіль',
    to: `${toUrl(pagePaths.profile)}?section=profile`,
  },
  {
    Icon: EmojiEventsIcon,
    label: 'Нагороди',
    to: `${toUrl(pagePaths.profile)}?section=awards`,
  },
  {
    Icon: BarChartIcon,
    label: 'Статистика',
    to: `${toUrl(pagePaths.profile)}?section=stats`,
  },
  {
    Icon: WorkspacePremiumIcon,
    label: 'Підписка',
    to: `${toUrl(pagePaths.profile)}?section=subscription`,
  },
  {
    Icon: SettingsIcon,
    label: 'Налаштування',
    to: `${toUrl(pagePaths.profile)}?section=settings`,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Layout constants
// ─────────────────────────────────────────────────────────────────────────────

// 1280px is well below MUI's `lg` (1580px in this theme) and above all common
// laptops (1366/1440/1536) — keeps the full nav on every modern laptop while
// folding into a hamburger on small tablets.
const HAMBURGER_BREAKPOINT = 1280;
const DESKTOP_NAV_MEDIA = `@media (min-width: ${HAMBURGER_BREAKPOINT}px)`;

// ─────────────────────────────────────────────────────────────────────────────
// Shared sx blocks
// ─────────────────────────────────────────────────────────────────────────────

const primaryHoverBgSx = (theme: any) =>
  theme.palette.mode === 'light'
    ? 'rgba(59, 44, 147, 0.06)'
    : 'rgba(155, 124, 255, 0.10)';

const primaryPressBgSx = (theme: any) =>
  theme.palette.mode === 'light'
    ? 'rgba(59, 44, 147, 0.12)'
    : 'rgba(155, 124, 255, 0.18)';

const profileAvatarSx = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 13,
  fontWeight: 700,
  height: 32,
  width: 32,
};

const themeToggleIconSx = {
  color: 'text.primary',
  cursor: 'pointer',
  display: 'block',
  height: 22,
  width: 22,
};

const drawerLinkSx = {
  color: 'text.primary',
  py: 1.25,
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'primary.main',
  },
};

const drawerNestedLinkSx = {
  ...drawerLinkSx,
  pl: 4,
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const getProfileButtonLabel = (profile?: Partial<UserProfile>) => {
  const firstName = profile?.firstName?.trim();
  const lastName = profile?.lastName?.trim();
  if (firstName && lastName) {
    return `${firstName} ${lastName.slice(0, 1).toUpperCase()}.`;
  }
  return firstName || profile?.email || 'Мій профіль';
};

const getProfileInitials = (label: string) => {
  if (!label || label === 'Мій профіль') return 'МП';
  const cleaned = label.replace(/[^\p{L}\p{N}\s]/gu, '').trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0]?.slice(0, 2) || '?').toUpperCase();
};

const stripQuery = (href: string) => href.split('?')[0];

const isActiveLink = (pathname: string, href: string) => {
  const target = stripQuery(href);
  if (target === '/') return pathname === '/';
  // Highlight not only exact matches but also nested sections (e.g.
  // /vocabulary/words/page/3 still keeps "Мій словник" lit). We accept
  // either equality or pathname starting with `target/`.
  return pathname === target || pathname.startsWith(`${target}/`);
};

// ─────────────────────────────────────────────────────────────────────────────
// MenuLink — icon (in rounded square) + label + optional description.
// Used inside the wordset mega-menu, vocabulary list, and profile panel.
// ─────────────────────────────────────────────────────────────────────────────

interface MenuLinkProps {
  Icon?: MenuIconComponent;
  label: string;
  desc?: string;
  to?: string;
  danger?: boolean;
  onClick?: () => void;
}

const MenuLink: React.FC<MenuLinkProps> = ({
  Icon,
  label,
  desc,
  to,
  danger,
  onClick,
}) => {
  const content = (
    <Box
      sx={(theme) => ({
        alignItems: desc ? 'flex-start' : 'center',
        borderRadius: 1,
        cursor: 'pointer',
        display: 'flex',
        gap: 1.5,
        px: 1.5,
        py: desc ? 1.25 : 1.125,
        textDecoration: 'none',
        transition: 'background-color .12s ease',
        '&:hover': {
          bgcolor: danger ? undefined : primaryHoverBgSx(theme),
          '& .ml-icon': {
            bgcolor: primaryPressBgSx(theme),
          },
          '& .ml-label': {
            color: danger ? 'secondary.main' : 'primary.main',
          },
        },
      })}
    >
      {Icon && (
        <Box
          className={'ml-icon'}
          sx={{
            alignItems: 'center',
            bgcolor: 'background.default',
            borderRadius: 1,
            color: danger ? 'secondary.main' : 'primary.main',
            display: 'inline-flex',
            flex: 'none',
            height: desc ? 38 : 30,
            justifyContent: 'center',
            transition: 'background-color .12s ease',
            width: desc ? 38 : 30,
          }}
        >
          <Icon sx={{ fontSize: desc ? 21 : 19 }} />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          className={'ml-label'}
          sx={{
            color: danger ? 'secondary.main' : 'text.primary',
            display: 'block',
            fontSize: 15,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {desc && (
          <Typography
            sx={{
              color: 'text.secondary',
              display: 'block',
              fontSize: 12.5,
              lineHeight: 1.35,
              mt: 0.25,
            }}
          >
            {desc}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (to) {
    return (
      <Box
        component={Link}
        to={to}
        onClick={onClick}
        sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        {content}
      </Box>
    );
  }
  return (
    <Box onClick={onClick} sx={{ display: 'block' }}>
      {content}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MenuPanel — anchored Popper shell with the polished card chrome the
// redesign uses (border + soft shadow + 8px padding). Wraps the existing
// MUI Popper so we keep `preventOverflow` and the hover-bridge behaviour
// (onMouseEnter/onMouseLeave on the paper) without rewriting from scratch.
// ─────────────────────────────────────────────────────────────────────────────

interface MenuPanelProps {
  id: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  placement?: 'bottom-start' | 'bottom-end' | 'bottom';
  width?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}

const MenuPanel: React.FC<MenuPanelProps> = ({
  id,
  open,
  anchorEl,
  placement = 'bottom-start',
  width,
  onMouseEnter,
  onMouseLeave,
  children,
}) => (
  <Popper
    id={id}
    open={open}
    anchorEl={anchorEl}
    placement={placement}
    // Portal to <body> on purpose — UICard wraps the header with overflow:hidden
    // (MUI Card default), which clips any in-place dropdown. The Paper below
    // keeps the hover bridge alive via its own onMouseEnter/onMouseLeave, so
    // portalling doesn't break the delayed-close behaviour.
    modifiers={[
      // Flush against the header bar — no visible gap between the trigger
      // and the dropdown. The header itself already provides the border
      // separation the eye needs.
      { name: 'offset', options: { offset: [0, 0] } },
      { name: 'preventOverflow', options: { padding: 12 } },
    ]}
    sx={{ zIndex: (theme) => theme.zIndex.modal }}
  >
    <Paper
      elevation={0}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
        p: 1,
        width,
      }}
    >
      {children}
    </Paper>
  </Popper>
);

// ─────────────────────────────────────────────────────────────────────────────
// Sticky shadow — attach to <body>/window scroll so the header gets a soft
// drop shadow once we've scrolled past ~8px. Keeps the visual cue from the
// prototype that the bar is floating above content.
// ─────────────────────────────────────────────────────────────────────────────

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
};

// ─────────────────────────────────────────────────────────────────────────────
// AppHeader
// ─────────────────────────────────────────────────────────────────────────────

const AppHeader: React.FC<IAppHeaderProps> = () => {
  const { logout } = useContext(AuthContext);
  const { mode, setMode } = useColorScheme();
  const location = useLocation();
  const scrolled = useScrolled();
  const isTestRunPage = isPathEqualToRoute(
    location.pathname,
    toUrl(pagePaths.vocabularyTestRun),
  );
  const activeMode = mode === 'dark' ? 'dark' : 'light';
  const closeMenuTimeoutRef = useRef<number | undefined>(undefined);
  const [vocabularyAnchorEl, setVocabularyAnchorEl] =
    useState<HTMLElement | null>(null);
  const [wordSetAnchorEl, setWordSetAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [profileButtonLabel, setProfileButtonLabel] = useState('Мій профіль');
  const [profileEmail, setProfileEmail] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerWordSetOpen, setDrawerWordSetOpen] = useState(false);
  const [drawerVocabularyOpen, setDrawerVocabularyOpen] = useState(false);
  const [drawerProfileOpen, setDrawerProfileOpen] = useState(false);
  const vocabularyMenuOpen = Boolean(vocabularyAnchorEl);
  const wordSetMenuOpen = Boolean(wordSetAnchorEl);
  const profileMenuOpen = Boolean(profileAnchorEl);

  // Active-link calculations live next to the menu data so triggers and
  // drawer items share a single source of truth.
  const navActive = useMemo(() => {
    const path = location.pathname;
    return {
      wordset:
        isActiveLink(path, toUrl(pagePaths.wordset)) ||
        wordSetMenuItems.some((it) => isActiveLink(path, it.to)),
      vocabulary:
        isActiveLink(path, toUrl(pagePaths.vocabulary)) ||
        vocabularyMenuItems.some((it) => isActiveLink(path, it.to)),
      learning: isActiveLink(path, toUrl(pagePaths.learning)),
      inAction: isActiveLink(path, toUrl(pagePaths.inAction)),
      community: isActiveLink(path, toUrl(pagePaths.community)),
      test:
        isPathEqualToRoute(path, toUrl(pagePaths.vocabularyTest)) ||
        isActiveLink(path, toUrl(pagePaths.vocabularyTest)),
    };
  }, [location.pathname]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data } = await axiosInstance.get<UserProfile>('/profile');
        setProfileButtonLabel(getProfileButtonLabel(data));
        setProfileEmail(data?.email ?? '');
      } catch {
        setProfileButtonLabel('Мій профіль');
      }
    };
    const handleProfileUpdated = (event: Event) => {
      const { detail } = event as CustomEvent<UserProfile>;
      setProfileButtonLabel(getProfileButtonLabel(detail));
      setProfileEmail(detail?.email ?? '');
    };

    loadProfile();
    window.addEventListener('profile-updated', handleProfileUpdated);
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdated);
    };
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const clearCloseMenuTimeout = () => {
    if (closeMenuTimeoutRef.current) {
      window.clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = undefined;
    }
  };

  const closeMenus = () => {
    clearCloseMenuTimeout();
    setVocabularyAnchorEl(null);
    setWordSetAnchorEl(null);
    setProfileAnchorEl(null);
  };

  const scheduleCloseMenus = () => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = window.setTimeout(closeMenus, 180);
  };

  const openVocabularyMenu = (event: MouseEvent<HTMLElement>) => {
    clearCloseMenuTimeout();
    setWordSetAnchorEl(null);
    setProfileAnchorEl(null);
    setVocabularyAnchorEl(event.currentTarget);
  };

  const openWordSetMenu = (event: MouseEvent<HTMLElement>) => {
    clearCloseMenuTimeout();
    setVocabularyAnchorEl(null);
    setProfileAnchorEl(null);
    setWordSetAnchorEl(event.currentTarget);
  };

  const openProfileMenu = (event: MouseEvent<HTMLElement>) => {
    clearCloseMenuTimeout();
    setVocabularyAnchorEl(null);
    setWordSetAnchorEl(null);
    setProfileAnchorEl(event.currentTarget);
  };

  const toggleThemeMode = () => {
    setMode(activeMode === 'light' ? 'dark' : 'light');
  };

  // ── Desktop nav trigger ────────────────────────────────────────────────────

  type TriggerProps = {
    label: string;
    to: string;
    active: boolean;
    hasMenu?: boolean;
    open?: boolean;
    onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
    onMouseLeave?: () => void;
  };

  const NavTrigger: React.FC<TriggerProps> = ({
    label,
    to,
    active,
    hasMenu,
    open,
    onMouseEnter,
    onMouseLeave,
  }) => (
    <Box sx={{ position: 'relative' }}>
      <Box
        component={Link}
        to={to}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          alignItems: 'center',
          color: active || open ? 'primary.main' : 'text.primary',
          cursor: 'pointer',
          display: 'inline-flex',
          fontSize: 15,
          fontWeight: 600,
          gap: 0.25,
          height: 64,
          position: 'relative',
          textDecoration: 'none',
          transition: 'color .12s ease',
          whiteSpace: 'nowrap',
          '&:hover': { color: 'primary.main' },
        }}
      >
        {label}
        {hasMenu && (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              mt: '1px',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .18s ease',
            }}
          />
        )}
        {active && (
          <Box
            sx={{
              bgcolor: 'primary.main',
              borderRadius: '3px',
              bottom: 0,
              height: '2.5px',
              left: 0,
              position: 'absolute',
              right: 0,
            }}
          />
        )}
      </Box>
    </Box>
  );

  // ── Mega-menu (wordset) ───────────────────────────────────────────────────

  const renderWordSetMenu = () => (
    <MenuPanel
      id={'wordset-menu'}
      open={wordSetMenuOpen}
      anchorEl={wordSetAnchorEl}
      placement={'bottom-start'}
      width={820}
      onMouseEnter={clearCloseMenuTimeout}
      onMouseLeave={scheduleCloseMenus}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 0.75,
          gridTemplateColumns: '1fr 1fr 1fr',
        }}
      >
        {wordSetMenuGroups.map((group) => (
          <Box key={group.title}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.07em',
                px: 1.5,
                pt: 1,
                pb: 0.75,
                textTransform: 'uppercase',
              }}
            >
              {group.title}
            </Typography>
            {group.items.map((item) => (
              <MenuLink
                key={item.to}
                Icon={item.Icon}
                label={item.label}
                desc={item.desc}
                to={item.to}
                onClick={closeMenus}
              />
            ))}
          </Box>
        ))}
      </Box>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          mt: 1,
          mx: 0.5,
          pt: 1,
        }}
      >
        <MenuLink
          Icon={AppsIcon}
          label={'Усі набори слів'}
          to={toUrl(pagePaths.wordset)}
          onClick={closeMenus}
        />
      </Box>
    </MenuPanel>
  );

  // ── List menu (vocabulary) ────────────────────────────────────────────────

  const renderVocabularyMenu = () => (
    <MenuPanel
      id={'vocabulary-menu'}
      open={vocabularyMenuOpen}
      anchorEl={vocabularyAnchorEl}
      placement={'bottom-start'}
      width={340}
      onMouseEnter={clearCloseMenuTimeout}
      onMouseLeave={scheduleCloseMenus}
    >
      {vocabularyMenuItems.map((item) => (
        <MenuLink
          key={item.to}
          Icon={item.Icon}
          label={item.label}
          to={item.to}
          onClick={closeMenus}
        />
      ))}
    </MenuPanel>
  );

  // ── Profile menu ──────────────────────────────────────────────────────────

  const renderProfileMenu = () => (
    <MenuPanel
      id={'profile-menu'}
      open={profileMenuOpen}
      anchorEl={profileAnchorEl}
      placement={'bottom-end'}
      width={280}
      onMouseEnter={clearCloseMenuTimeout}
      onMouseLeave={scheduleCloseMenus}
    >
      {/* User card */}
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1.25}
        sx={{ px: 1.25, pt: 1, pb: 1.25 }}
      >
        <Avatar sx={{ ...profileAvatarSx, height: 42, width: 42, fontSize: 15 }}>
          {getProfileInitials(profileButtonLabel)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {profileButtonLabel}
          </Typography>
          {profileEmail && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 12.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {profileEmail}
            </Typography>
          )}
        </Box>
      </Stack>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          pt: 0.5,
        }}
      >
        {profileMenuItems.map((item) => (
          <MenuLink
            key={item.to}
            Icon={item.Icon}
            label={item.label}
            to={item.to}
            onClick={closeMenus}
          />
        ))}
      </Box>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          mt: 0.5,
          pt: 0.5,
        }}
      >
        <MenuLink
          Icon={LogoutIcon}
          label={'Вийти'}
          danger
          onClick={() => {
            closeMenus();
            logout();
          }}
        />
      </Box>
    </MenuPanel>
  );

  // ── Drawer pieces ─────────────────────────────────────────────────────────

  const renderDrawerItemLink = (item: MenuItem) => (
    <ListItemButton
      key={item.to}
      component={Link}
      to={item.to}
      sx={drawerNestedLinkSx}
      onClick={() => setDrawerOpen(false)}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          borderRadius: 1,
          color: 'primary.main',
          display: 'inline-flex',
          flex: 'none',
          height: 28,
          justifyContent: 'center',
          mr: 1.5,
          width: 28,
        }}
      >
        <item.Icon sx={{ fontSize: 18 }} />
      </Box>
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{ fontSize: 15 }}
      />
    </ListItemButton>
  );

  const renderDrawerGroup = (
    label: string,
    rootTo: string,
    allLabel: string,
    open: boolean,
    toggle: () => void,
    items: MenuItem[],
  ) => (
    <>
      <ListItemButton sx={drawerLinkSx} onClick={toggle}>
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontSize: 16, fontWeight: 600 }}
        />
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={open} timeout={'auto'} unmountOnExit>
        <List component={'div'} disablePadding>
          <ListItemButton
            component={Link}
            to={rootTo}
            sx={drawerNestedLinkSx}
            onClick={() => setDrawerOpen(false)}
          >
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'background.default',
                borderRadius: 1,
                color: 'primary.main',
                display: 'inline-flex',
                flex: 'none',
                height: 28,
                justifyContent: 'center',
                mr: 1.5,
                width: 28,
              }}
            >
              <AppsIcon sx={{ fontSize: 18 }} />
            </Box>
            <ListItemText
              primary={allLabel}
              primaryTypographyProps={{
                color: 'primary.main',
                fontSize: 15,
                fontWeight: 600,
              }}
            />
          </ListItemButton>
          {items.map(renderDrawerItemLink)}
        </List>
      </Collapse>
    </>
  );

  const renderDrawerPlainLink = (
    label: string,
    to: string,
    active: boolean,
  ) => (
    <ListItemButton
      component={Link}
      to={to}
      sx={(theme) => ({
        ...drawerLinkSx,
        bgcolor: active ? primaryHoverBgSx(theme) : 'transparent',
        borderLeft: '3px solid',
        borderColor: active ? 'primary.main' : 'transparent',
        pl: active ? 1.625 : 2,
      })}
      onClick={() => setDrawerOpen(false)}
    >
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          color: active ? 'primary.main' : 'text.primary',
          fontSize: 16,
          fontWeight: 600,
        }}
      />
    </ListItemButton>
  );

  const renderMobileDrawer = () => (
    <Drawer
      anchor={'right'}
      disableScrollLock
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: '85vw', sm: 360 },
          maxWidth: 400,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <UIFlex
        jc={'space-between'}
        ai={'center'}
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <UIText variant={'h6'} sx={{ fontWeight: 700 }}>
          {'Меню'}
        </UIText>
        <IconButton
          aria-label={'Закрити меню'}
          onClick={() => setDrawerOpen(false)}
          edge={'end'}
        >
          <CloseIcon />
        </IconButton>
      </UIFlex>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <List disablePadding sx={{ pt: 0.5 }}>
          {renderDrawerGroup(
            'Набори слів',
            toUrl(pagePaths.wordset),
            'Усі набори слів',
            drawerWordSetOpen,
            () => setDrawerWordSetOpen((prev) => !prev),
            wordSetMenuItems,
          )}
          {renderDrawerGroup(
            'Мій словник',
            toUrl(pagePaths.vocabulary),
            'Весь словник',
            drawerVocabularyOpen,
            () => setDrawerVocabularyOpen((prev) => !prev),
            vocabularyMenuItems,
          )}
          {renderDrawerPlainLink(
            'Вправи на вивчення',
            toUrl(pagePaths.learning),
            navActive.learning,
          )}
          {renderDrawerPlainLink(
            'В дії',
            toUrl(pagePaths.inAction),
            navActive.inAction,
          )}
          {renderDrawerPlainLink(
            'Тест словникового запасу',
            toUrl(pagePaths.vocabularyTest),
            navActive.test,
          )}
          {renderDrawerPlainLink(
            'Спільнота',
            toUrl(pagePaths.community),
            navActive.community,
          )}
        </List>

        <Box sx={{ mt: 'auto' }}>
          <Divider />
          <List disablePadding>
            <ListItemButton
              sx={drawerLinkSx}
              onClick={() => setDrawerProfileOpen((prev) => !prev)}
            >
              <ListItemAvatar sx={{ minWidth: 50 }}>
                <Avatar sx={{ ...profileAvatarSx, height: 38, width: 38 }}>
                  {getProfileInitials(profileButtonLabel)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={profileButtonLabel}
                secondary={profileEmail || undefined}
                primaryTypographyProps={{ fontSize: 15, fontWeight: 700 }}
                secondaryTypographyProps={{
                  fontSize: 12.5,
                  noWrap: true,
                }}
              />
              {drawerProfileOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={drawerProfileOpen} timeout={'auto'} unmountOnExit>
              <List component={'div'} disablePadding>
                {profileMenuItems.map((item) => (
                  <ListItemButton
                    key={item.to}
                    component={Link}
                    to={item.to}
                    sx={drawerNestedLinkSx}
                    onClick={() => setDrawerOpen(false)}
                  >
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'inline-flex',
                        flex: 'none',
                        mr: 1.5,
                      }}
                    >
                      <item.Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: 15 }}
                    />
                  </ListItemButton>
                ))}
                <ListItemButton
                  sx={drawerNestedLinkSx}
                  onClick={() => {
                    setDrawerOpen(false);
                    logout();
                  }}
                >
                  <Box
                    sx={{
                      color: 'secondary.main',
                      display: 'inline-flex',
                      flex: 'none',
                      mr: 1.5,
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <ListItemText
                    primary={'Вийти'}
                    primaryTypographyProps={{
                      color: 'secondary.main',
                      fontSize: 15,
                      fontWeight: 600,
                    }}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </List>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <UICard
      sx={{
        borderRadius: 0,
        // Soft permanent shadow so the white header reads as "chrome"
        // floating above the gray page body — without it the bar can
        // visually fuse with white cards underneath. On scroll the
        // shadow deepens to confirm the bar is sticky.
        boxShadow: scrolled
          ? '0 6px 20px -12px rgba(20, 20, 20, 0.35)'
          : '0 1px 3px rgba(20, 20, 20, 0.06)',
        // Override MUI Card's default overflow:hidden — without this any
        // inline dropdown rendered inside the header gets clipped at the
        // card border. Portalled dropdowns (Popper without disablePortal,
        // Menu, Drawer) are unaffected, but the override keeps the door
        // open for inline anchored elements (e.g. AppLanguageSelect).
        overflow: 'visible',
        // The active vocabulary-test screen has its own floating sticky bar
        // (status actions + finish) directly under the header — stacking a
        // sticky header on top of that reads as one bar fighting another for
        // the same space, so the header stays in normal flow there instead.
        position: isTestRunPage ? 'static' : 'sticky',
        px: 0,
        py: 0,
        top: 0,
        transition: 'box-shadow .2s ease',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <UIContainer>
        <UIFlex
          ai={'center'}
          columnSpacing={{ xs: 1, sm: 2 }}
          flexWrap={'nowrap'}
          sx={{ minHeight: { xs: 56, sm: 60, [DESKTOP_NAV_MEDIA]: 64 } }}
        >
          {/* Logo */}
          <Link to={'/'} onMouseEnter={closeMenus}>
            <Box
              component={Logo}
              sx={{
                color: 'primary.main',
                display: 'block',
                height: { xs: 40, sm: 44, lg: 'auto' },
                width: 'auto',
              }}
            />
          </Link>

          {/* Desktop nav — centered between logo and right cluster */}
          <Box
            sx={{
              display: 'none',
              flex: 1,
              [DESKTOP_NAV_MEDIA]: {
                alignItems: 'center',
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
              },
            }}
          >
            <Box
              onMouseEnter={openWordSetMenu}
              onMouseLeave={scheduleCloseMenus}
              sx={{ position: 'relative' }}
            >
              <NavTrigger
                label={'Набори слів'}
                to={toUrl(pagePaths.wordset)}
                active={navActive.wordset}
                hasMenu
                open={wordSetMenuOpen}
              />
              {renderWordSetMenu()}
            </Box>

            <Box
              onMouseEnter={openVocabularyMenu}
              onMouseLeave={scheduleCloseMenus}
              sx={{ position: 'relative' }}
            >
              <NavTrigger
                label={'Мій словник'}
                to={toUrl(pagePaths.vocabulary)}
                active={navActive.vocabulary}
                hasMenu
                open={vocabularyMenuOpen}
              />
              {renderVocabularyMenu()}
            </Box>

            <NavTrigger
              label={'Вправи на вивчення'}
              to={toUrl(pagePaths.learning)}
              active={navActive.learning}
              onMouseEnter={closeMenus}
            />
            <NavTrigger
              label={'В дії'}
              to={toUrl(pagePaths.inAction)}
              active={navActive.inAction}
              onMouseEnter={closeMenus}
            />
            <NavTrigger
              label={'Тест словникового запасу'}
              to={toUrl(pagePaths.vocabularyTest)}
              active={navActive.test}
              onMouseEnter={closeMenus}
            />
            {/*<NavTrigger*/}
            {/*  label={'Спільнота'}*/}
            {/*  to={toUrl(pagePaths.community)}*/}
            {/*  active={navActive.community}*/}
            {/*  onMouseEnter={closeMenus}*/}
            {/*/>*/}
          </Box>

          {/* Right cluster */}
          <UIFlex
            ai={'center'}
            columnSpacing={{ xs: 0.5, sm: 1, lg: 1.25 }}
            flexWrap={'nowrap'}
            sx={{ marginLeft: 'auto', width: 'auto' }}
          >
            <IconButton
              aria-label={
                activeMode === 'light'
                  ? 'Увімкнути нічну тему'
                  : 'Увімкнути денну тему'
              }
              onClick={toggleThemeMode}
              sx={(theme) => ({
                borderRadius: 1.25,
                height: { xs: 38, lg: 40 },
                width: { xs: 38, lg: 40 },
                transition: 'background-color .12s ease',
                '&:hover': { bgcolor: primaryHoverBgSx(theme) },
              })}
            >
              {activeMode === 'light' ? (
                <Box
                  component={DarkThemeIcon}
                  sx={{
                    ...themeToggleIconSx,
                    height: { xs: 20, sm: 22 },
                    width: { xs: 20, sm: 22 },
                  }}
                />
              ) : (
                <Box
                  component={LightThemeIcon}
                  sx={{
                    ...themeToggleIconSx,
                    height: { xs: 20, sm: 22 },
                    width: { xs: 20, sm: 22 },
                  }}
                />
              )}
            </IconButton>

            <AppLanguageSelect />

            {/* Vertical divider before profile — desktop only */}
            <Box
              sx={(theme) => ({
                bgcolor: theme.palette.primary.tableBorder,
                display: 'none',
                flex: 'none',
                height: 26,
                mx: 0.5,
                width: '1px',
                [DESKTOP_NAV_MEDIA]: { display: 'block' },
              })}
            />

            {/* Profile trigger — desktop only. Split into two layers:
                an outer anchor box that fills the full bar height (so the
                Popper aligns with the other dropdowns), and an inner
                button-shaped pill that owns the visible chrome + hover
                state. Without the split, hovering the trigger painted
                the entire 64 px column with the primary tint and looked
                inflated. */}
            <Box
              aria-controls={profileMenuOpen ? 'profile-menu' : undefined}
              aria-expanded={profileMenuOpen ? 'true' : undefined}
              aria-haspopup={'true'}
              aria-label={'Профіль'}
              component={Link}
              onMouseEnter={openProfileMenu}
              onMouseLeave={scheduleCloseMenus}
              role={'button'}
              tabIndex={0}
              to={`${toUrl(pagePaths.profile)}?section=profile`}
              sx={{
                alignItems: 'center',
                color: 'text.primary',
                cursor: 'pointer',
                display: 'none',
                height: 64,
                textDecoration: 'none',
                [DESKTOP_NAV_MEDIA]: { display: 'inline-flex' },
                '& .profile-trigger-label': {
                  maxWidth: 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                // Pull hover styling onto the inner pill via cascade so
                // pointer movement anywhere inside the 64 px column lights
                // up the small button, not the whole column.
                '&:hover .profile-trigger-pill, & .profile-trigger-pill[data-open="true"]':
                  {
                    bgcolor: (theme) => primaryHoverBgSx(theme),
                    color: 'primary.main',
                  },
              }}
            >
              <Box
                className={'profile-trigger-pill'}
                data-open={profileMenuOpen}
                sx={{
                  alignItems: 'center',
                  borderRadius: 999,
                  display: 'inline-flex',
                  gap: 1,
                  pl: 0.5,
                  pr: 1.25,
                  py: 0.5,
                  transition: 'background-color .15s ease, color .15s ease',
                }}
              >
                <Avatar sx={profileAvatarSx}>
                  {getProfileInitials(profileButtonLabel)}
                </Avatar>
                <UIText
                  className={'profile-trigger-label'}
                  component={'span'}
                  sx={{ fontSize: 15, fontWeight: 600 }}
                >
                  {profileButtonLabel}
                </UIText>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 18,
                    color: 'text.secondary',
                    transform: profileMenuOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform .18s ease',
                  }}
                />
              </Box>
            </Box>
            {renderProfileMenu()}

            {/* Hamburger — mobile/tablet */}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                [DESKTOP_NAV_MEDIA]: { display: 'none' },
              }}
            >
              <IconButton
                aria-label={'Відкрити меню'}
                onClick={() => setDrawerOpen(true)}
                edge={'end'}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </UIFlex>
        </UIFlex>
      </UIContainer>

      {renderMobileDrawer()}
    </UICard>
  );
};

export default AppHeader;
